import { useSnackbar } from 'notistack'
import { ChangeEvent, useRef, useState } from 'react'
import { IconButton } from '@mui/material'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import ClearIcon from '@mui/icons-material/Clear'
import CustomInput from '../../components/TextInput'
import CustomCheckbox from '../../components/Checkbox'
import CustomButton from '../../components/Button'
import { CommunityDTO } from '../../models/community'
import CommunitiesService from '../../services/CommunitiesService'

const DEFAULT_COMMUNITY: CommunityDTO = {
    id: '',
    name: '',
    description: '',
    image: '',
    isPrivate: false,
    members: [],
    administrators: [],
    pendingMembers: [],
    founder: undefined,
}

const DEFAULT_ERRORS = {
    name: '',
    description: '',
}

interface Props {
    info?: CommunityDTO
    handleClose: () => void
    mutate?: boolean
    setMutate?: (arg: boolean) => void
}
const CommunityForm = ({ info, handleClose, mutate = false, setMutate = () => {} }: Props) => {
    const [community, setCommunity] = useState<CommunityDTO>(info ?? DEFAULT_COMMUNITY)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [errors, setErrors] = useState<typeof DEFAULT_ERRORS>(DEFAULT_ERRORS)
    const { enqueueSnackbar } = useSnackbar()
    const [inputKey, setInputKey] = useState(0)
    const [loading, setLoading] = useState<boolean>(false)

    const handleOnChange = (name: string, value: string, files?: FileList) => {
        if (files && files[0]) {
            const file = files[0]
            const reader = new FileReader()

            reader.onloadend = () => {
                const base64String = reader.result as string

                setCommunity((prev) => ({
                    ...prev,
                    image: base64String,
                }))
            }

            reader.readAsDataURL(file)
        } else {
            setCommunity((prev) => ({ ...prev, [name]: value }))
        }
    }

    const validateInfo = () => {
        const newErrors = {
            name: community.name === '' ? 'Completá este campo' : '',
            description: community.description === '' ? 'Completá este campo' : '',
        }

        setErrors(newErrors)

        return Object.values(newErrors).every((x) => x === '')
    }

    const handleSubmit = async () => {
        if (!validateInfo()) return
        setLoading(true)
        let response
        if (info) response = await new CommunitiesService().editCommunity(community)
        else response = await new CommunitiesService().createCommunity(community)

        if (response.status === 200) {
            enqueueSnackbar(info ? 'Comunidad editada' : 'Comunidad creada', {
                variant: 'success',
                autoHideDuration: 6000,
            })
            setMutate(!mutate)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }

        handleClose()
    }

    const handleImageRemove = () => {
        setCommunity((prevState) => ({ ...prevState, image: '' }))
        setInputKey((prevKey) => prevKey + 1)
    }

    const handleCheckbox = (value: boolean) => {
        setCommunity((prevState) => ({ ...prevState, isPrivate: value }))
    }

    return (
        <div className="flex flex-col w-[70vw] md:w-[35vw] overflow-auto max-h-[50vh] md:max-h-[80vh] md:px-2 gap-2 md:gap-4 text-error">
            <div className="flex justify-between items-center">
                <label
                    className="text-secondary font-semibold"
                    htmlFor="postInput"
                >
                    {info ? 'Editar comunidad' : 'Nueva comunidad'}
                </label>
                <div className="flex items-center">
                    <CustomInput
                        key={inputKey}
                        id="image"
                        name="image"
                        type="file"
                        ref={fileInputRef}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleOnChange(
                                e.target.name,
                                e.target.value,
                                e.target.files as FileList,
                            )
                        }
                        className="hidden"
                        inputProps={{ accept: 'image/*' }}
                    />
                    <label htmlFor="image">
                        <IconButton
                            className="text-primary"
                            component="span"
                        >
                            <AddPhotoAlternateIcon />
                        </IconButton>
                    </label>
                    <IconButton
                        className="text-primary"
                        onClick={handleClose}
                    >
                        <ClearIcon />
                    </IconButton>
                </div>
            </div>
            <CustomInput
                label="Nombre"
                name="name"
                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                value={community.name}
                errorText={errors.name}
                error={errors.name !== ''}
                multiline
            />
            <CustomInput
                label="Descripción"
                name="description"
                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                value={community.description}
                errorText={errors.description}
                error={errors.description !== ''}
                className="w-full border"
                multiline
                minRows={3}
            />
            <div className="flex gap-2 md:gap-4">
                <div className="flex gap-1 md:gap-2 ml-2 md:ml-4 items-center text-[#777777] w-[20%]">
                    Privada
                    <CustomCheckbox
                        checked={community.isPrivate}
                        className="text-[#666666] p-0"
                        onChange={(e) => handleCheckbox(e.target.checked)}
                    />
                </div>
            </div>
            {community.image && (
                <div className="flex gap-2">
                    <div>
                        <img
                            alt="community"
                            src={community.image}
                            className="overflow-y-auto max-h-[250px] max-w-[225px] md:max-w-full"
                        />
                    </div>
                    <IconButton
                        onClick={handleImageRemove}
                        className="text-white bg-black p-0 rounded-full h-6"
                    >
                        <HighlightOffIcon />
                    </IconButton>
                </div>
            )}
            <CustomButton
                className={community.image ?? 'mt-4'}
                text={info ? 'Editar comunidad' : 'Crear comunidad'}
                onClick={handleSubmit}
                disabled={!community.name || !community.description}
                loading={loading}
            />
        </div>
    )
}

export default CommunityForm
